import React, { useState } from 'react';
import { Eye, EyeOff, type LucideIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';

interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: LucideIcon;
  error?: string | null;
  hint?: string;
}

export const AuthInput: React.FC<AuthInputProps> = ({ 
  label, 
  icon: Icon, 
  error, 
  hint, 
  type, 
  className,
  ...props 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className="space-y-1.5 group">
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 transition-colors group-focus-within:text-brand-primary">
        {label}
      </label>
      <div className="relative">
        <Icon className={cn(
          "absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300",
          error ? 'text-red-500' : 'text-gray-500 group-focus-within:text-brand-primary'
        )} size={18} />
        <input 
          {...props}
          type={inputType}
          className={cn(
            "w-full bg-white/5 border border-white/10 rounded-xl py-3.5 outline-none transition-all duration-300 text-white placeholder-gray-600 hover:bg-white/10 ring-0 focus:ring-2 focus:ring-brand-primary/20",
            "pl-12",
            isPassword ? "pr-12" : "pr-4",
            error ? 'border-red-500/50 focus:border-red-500 shadow-sm shadow-red-500/10' : 'focus:border-brand-primary focus:bg-brand-primary/5',
            className
          )}
        />
        
        {isPassword && (
          <button 
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-1"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      
      <AnimatePresence>
        {error && (
          <motion.p 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="text-red-400 text-[11px] font-medium ml-1.5 pt-1"
          >
            {error}
          </motion.p>
        )}
        {!error && hint && (
          <p className="text-gray-500 text-[10px] ml-1.5 pt-1 italic">{hint}</p>
        )}
      </AnimatePresence>
    </div>
  );
};
